import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, ApiError, jsonBody } from './api'
import { useAuth } from './auth'
import { DotItBrand } from './brand'
import type { SessionResponse, TenantRole } from './types'

interface InvitationDetails {
  email: string
  organizationName: string
  role: TenantRole
  expiresAt: string
  existingAccount: boolean
  mfaRequired: boolean
  organizationMfaEnforced: boolean
}

const roleLabel: Record<TenantRole, string> = {
  TENANT_OWNER: 'Owner',
  TENANT_ADMIN: 'Administrator',
  CONTENT_MANAGER: 'Content manager',
  DEVICE_MANAGER: 'Device manager',
  VIEWER: 'Viewer',
}

export function InvitationPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const { establish } = useAuth()
  const token = new URLSearchParams(location.search).get('token')
  const [details, setDetails] = useState<InvitationDetails>()
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!token) {
      setError('The invitation token is missing.')
      return
    }
    void api<InvitationDetails>('/auth/invitations/inspect', {
      method: 'POST',
      body: jsonBody({ token }),
    })
      .then(setDetails)
      .catch((reason: unknown) => {
        setError(
          reason instanceof ApiError
            ? reason.message
            : 'The invitation could not be opened.'
        )
      })
  }, [token])

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!token || !details) return
    setBusy(true)
    const data = new FormData(event.currentTarget)
    try {
      const session = await api<SessionResponse>('/auth/invitations/accept', {
        method: 'POST',
        body: jsonBody({
          token,
          name: details.existingAccount ? undefined : data.get('name'),
          password: data.get('password'),
          mfaCode: details.mfaRequired ? data.get('mfaCode') : undefined,
        }),
      })
      establish(session)
      toast.success(`Welcome to ${details.organizationName}.`)
      navigate(details.organizationMfaEnforced ? '/app/security' : '/app', {
        replace: true,
      })
    } catch (reason) {
      toast.error(
        reason instanceof ApiError
          ? reason.message
          : 'The invitation could not be accepted.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className='relative grid min-h-svh place-items-center overflow-hidden bg-zinc-950 px-4 py-12 text-zinc-50'>
      <div className='absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(239,68,68,.18),transparent_34%),radial-gradient(circle_at_85%_80%,rgba(255,255,255,.08),transparent_30%)]' />
      <div className='relative w-full max-w-md'>
        <Link
          to='/'
          className='mb-7 inline-flex'
          aria-label='DOT IT Signage home'
        >
          <DotItBrand className='[&_span]:text-zinc-50' />
        </Link>
        <Card className='border-white/10 bg-white text-zinc-950 shadow-2xl'>
          <CardHeader>
            <CardTitle>
              {error
                ? 'Invitation unavailable'
                : details
                  ? `Join ${details.organizationName}`
                  : 'Opening invitation…'}
            </CardTitle>
            <CardDescription>
              {error ??
                (details
                  ? `${details.email} was invited as ${roleLabel[details.role]}.`
                  : 'Checking this secure invitation.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error ? (
              <Button asChild variant='outline' className='w-full'>
                <Link to='/login'>Return to sign in</Link>
              </Button>
            ) : details ? (
              <form className='space-y-4' onSubmit={submit}>
                {!details.existingAccount && (
                  <div className='space-y-2'>
                    <Label htmlFor='name'>Your name</Label>
                    <Input
                      id='name'
                      name='name'
                      autoComplete='name'
                      required
                      autoFocus
                    />
                  </div>
                )}
                <div className='space-y-2'>
                  <Label htmlFor='password'>
                    {details.existingAccount
                      ? 'Current password'
                      : 'Create a password'}
                  </Label>
                  <Input
                    id='password'
                    name='password'
                    type='password'
                    minLength={12}
                    autoComplete={
                      details.existingAccount
                        ? 'current-password'
                        : 'new-password'
                    }
                    required
                    autoFocus={details.existingAccount}
                  />
                  {!details.existingAccount && (
                    <p className='text-xs text-muted-foreground'>
                      Use at least 12 characters.
                    </p>
                  )}
                </div>
                {details.mfaRequired && (
                  <div className='space-y-2'>
                    <Label htmlFor='mfaCode'>Authenticator code</Label>
                    <Input
                      id='mfaCode'
                      name='mfaCode'
                      inputMode='numeric'
                      autoComplete='one-time-code'
                      pattern='[0-9]{6}'
                      maxLength={6}
                      required
                    />
                  </div>
                )}
                <Button
                  className='w-full bg-red-500 text-white hover:bg-red-600'
                  disabled={busy}
                >
                  {busy ? 'Joining…' : 'Accept invitation'}
                </Button>
              </form>
            ) : (
              <div
                className='h-1.5 overflow-hidden rounded-full bg-zinc-100'
                aria-label='Loading invitation'
              >
                <div className='h-full w-1/2 animate-pulse rounded-full bg-red-500' />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
